import { useCallback, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";

import { Button, Buttons, LoadingGroup, LoadingSkeleton, Message, Messages, Tag, TagType } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { CaseTags } from "capitalrx-types";
import { formatDate } from "capitalrx-utilities";

import { useGetPriorAuthorizationCaseQuery, useListPriorAuthorizationDecisionLetter } from "@/App/api/priorAuthorization";
import { PrintableWrapper } from "@/App/components";
import { ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { getCustomerSupportNumber } from "@/App/utils/utils";


const TRANSLATION_PATH = "PRINT_PRIOR_AUTHORIZATION_DETAILS_PAGE";

export const PrintPriorAuthorizationDetailsPage = () => {
  const { t: translate } = useTranslation();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { priorAuthorizationPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = priorAuthorizationPhoneNumber ? priorAuthorizationPhoneNumber : getCustomerSupportNumber(memberEligibility);
  const [searchParams, _] = useSearchParams();
  const pa_case_id = searchParams.get("pa_case_id");
  const printRef = useRef();
  const { data: priorAuthorizationCaseData, isLoading: priorAuthorizationCaseIsLoading } = useGetPriorAuthorizationCaseQuery({
    id: +pa_case_id,
  });
  const { data: documentListData, isLoading, isError } = useListPriorAuthorizationDecisionLetter({ case_id: +pa_case_id });
  const caseOutcomesObject = {
    approved: [translate(`${TRANSLATION_PATH}.approved`), "success-high-contrast"],
    denied: [translate(`${TRANSLATION_PATH}.denied`), "error-high-contrast"],
    closed: [translate(`${TRANSLATION_PATH}.closed`), "info-high-contrast"],
    in_progress: [translate(`${TRANSLATION_PATH}.inProgress`), "warning-high-contrast"],
  };
  const caseOutcome = priorAuthorizationCaseData?.case_outcome?.outcome || "in_progress";

  const dateFormat = "YYYY-MM-DD";

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const onPrint = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.PRIOR_AUTHORIZATION.printPage);
    handlePrint();
  }, [handlePrint]);

  const renderOutcomeSection = () => {
    if (priorAuthorizationCaseData?.case_outcome?.outcome === "approved") {
      return (
        <>
          <Body>
            <Semibold>{translate(`${TRANSLATION_PATH}.approved`)}: </Semibold>
            {formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}
          </Body>
          <Body>
            <Semibold>{translate(`${TRANSLATION_PATH}.coverageStarts`)}: </Semibold>
            {formatDate(priorAuthorizationCaseData?.prior_authorization?.effective_date, dateFormat)}
          </Body>
          <Body>
            <Semibold>{translate(`${TRANSLATION_PATH}.coverageExpires`)}: </Semibold>
            {formatDate(priorAuthorizationCaseData?.prior_authorization?.expiration_date, dateFormat)}
          </Body>
          {priorAuthorizationCaseData?.is_renewal_available && priorAuthorizationCaseData?.renewal_start_date && (
            <Body>
              <Semibold>{translate(`${TRANSLATION_PATH}.renewalAvailable`)}: </Semibold>
              {formatDate(priorAuthorizationCaseData?.renewal_start_date, dateFormat)}
            </Body>
          )}
        </>
      );
    }

    if (priorAuthorizationCaseData?.case_outcome?.outcome === "denied") {
      return (
        <Stack spacing="medium">
          <Stack spacing="mini">
            <Body>
              <Semibold>{translate(`${TRANSLATION_PATH}.denied`)}: </Semibold>
              {formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}
            </Body>
            {priorAuthorizationCaseData?.case_outcome?.denial_type != null && (
              <Body>
                <Semibold>{translate(`${TRANSLATION_PATH}.denialType`)}: </Semibold>
                {translate(`${TRANSLATION_PATH}.denialTypes.${priorAuthorizationCaseData?.case_outcome?.denial_type}`)}
              </Body>
            )}
            {priorAuthorizationCaseData?.case_outcome?.denial_reason != null && (
              <Body>
                <Semibold>{translate(`${TRANSLATION_PATH}.denialReason`)}: </Semibold>
                {translate(`${TRANSLATION_PATH}.denialReasons.${priorAuthorizationCaseData?.case_outcome?.denial_reason}`)}
              </Body>
            )}
          </Stack>
          <Body>
            {translate(`${TRANSLATION_PATH}.moreInformationSeeDocuments`)}
          </Body>
        </Stack>
      );
    }

    if (priorAuthorizationCaseData?.case_outcome?.outcome === "closed") {
      return (
        <Body>
          <Semibold>{translate(`${TRANSLATION_PATH}.closed`)}: </Semibold>
          {formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}
        </Body>
      );
    }

    return null;
  };

  return (
    <LoadingGroup loading={priorAuthorizationCaseIsLoading}>
      <PrintableWrapper ref={printRef} innerPadding="2.5rem">
        <LoadingGroup.Placeholder fallback={<PADetailsSkeleton />}>
          <Shelf alignment="span">
            <Shelf.Spacer />
            <Button appearance={Buttons.Functional.Tertiary.Large} icon="print" onClick={onPrint} />
          </Shelf>
          <Stack spacing="large">
            <Shelf alignment="top">
              <Stack spacing="mini" alignment="left">
                <SectionHeading>{translate(`${TRANSLATION_PATH}.priorAuthorizationCase`)}</SectionHeading>
                <Shelf alignment="center">
                  <Subtitle testId="prior-authorization">{priorAuthorizationCaseData?.drug_name}</Subtitle>
                  <Shelf alignment="center" spacing="mini">
                    {priorAuthorizationCaseData?.tags.includes(CaseTags.is_awaiting_prescriber_response) && caseOutcome == "in_progress" ? (
                      <Tag type="warning-high-contrast">{translate("PRIOR_AUTHORIZATION.awaitingResponse")}</Tag>
                    ) : (
                      <Tag type={caseOutcomesObject[caseOutcome]?.[1] as TagType}>{caseOutcomesObject[caseOutcome]?.[0]}</Tag>
                    )}
                    {priorAuthorizationCaseData?.is_renewal_available && <Tag type="system-alternative-high-contrast">
                      {translate("PRIOR_AUTHORIZATION.priorAuthorizationDetailsPage.renewalAvailable")}
                    </Tag>}
                    {priorAuthorizationCaseData?.next_case_review_level === "renewal" && <Tag type="info">
                      {translate("PRIOR_AUTHORIZATION.priorAuthorizationDetailsPage.renewalRequested")}
                    </Tag>}
                  </Shelf>
                  <Shelf.Spacer />
                </Shelf>
              </Stack>
              <Shelf.Spacer />
            </Shelf>
            {caseOutcome == "in_progress"&& priorAuthorizationCaseData?.tags.includes(CaseTags.is_awaiting_prescriber_response) && (
              <Message appearance={Messages.Informational} visible>
                {translate(`${TRANSLATION_PATH}.additionalInformationAndDocumentation`)}
              </Message>
            )}
            <Stack spacing="mini">
              <Body>
                <Semibold>{translate(`${TRANSLATION_PATH}.id`) }: </Semibold>
                {priorAuthorizationCaseData?.case_id}
              </Body>
              <Body>
                <Semibold>{translate(`${TRANSLATION_PATH}.requested`)}: </Semibold>
                {formatDate(priorAuthorizationCaseData?.received_at, dateFormat, true)}
              </Body>
              {renderOutcomeSection()}
            </Stack>
            <Stack spacing="small">
              <SectionHeading>{translate(`${TRANSLATION_PATH}.drugInformation`)}</SectionHeading>
              <Stack spacing="mini">
                <Heading>{priorAuthorizationCaseData?.drug_name}</Heading>
                <Body>
                  <Semibold>{translate(`${TRANSLATION_PATH}.quantity`)}: </Semibold>
                  {priorAuthorizationCaseData?.drug_quantity}
                </Body>
                <Body>
                  <Semibold>{translate(`${TRANSLATION_PATH}.daysSupply`)}: </Semibold>
                  {priorAuthorizationCaseData?.drug_days_supply}
                </Body>
                <Body>
                  <Semibold>{translate(`${TRANSLATION_PATH}.directionsForUse`)}: </Semibold>
                  {priorAuthorizationCaseData?.drug_directions_for_use}
                </Body>
              </Stack>
            </Stack>
            <Stack spacing="small">
              <SectionHeading>{translate(`${TRANSLATION_PATH}.prescriber`)}</SectionHeading>
              <Stack spacing="mini">
                <Heading>
                  {priorAuthorizationCaseData?.prescriber_first_name + " " + priorAuthorizationCaseData?.prescriber_last_name}
                </Heading>
                <Body>
                  <Semibold>{translate(`${TRANSLATION_PATH}.npi`)}: </Semibold>
                  {priorAuthorizationCaseData?.prescriber_npi}
                </Body>
                <Body>
                  <Semibold>{translate(`${TRANSLATION_PATH}.phone`)}: </Semibold>
                  <a href={`tel:${priorAuthorizationCaseData?.prescriber_business_practice_phone}`}>
                    {priorAuthorizationCaseData?.prescriber_business_practice_phone}
                  </a>
                </Body>
              </Stack>
            </Stack>
            <Stack spacing="small">
              <SectionHeading>{translate(`${TRANSLATION_PATH}.documents`)}</SectionHeading>
              {!isLoading && !isError && (
                <Stack spacing="small" alignment="left">
                  {+documentListData?.result_count == 0 && <Body> {translate(`${TRANSLATION_PATH}.none`)} </Body>}
                  {documentListData?.results
                    && +documentListData?.result_count >= 0
                    && documentListData.results.map((pa_document) => (
                      <Button key={pa_document.letter.uuid} appearance={Buttons.Functional.Tertiary.Compact} icon="link-out">
                        {pa_document.letter.filename}
                      </Button>
                    ))}
                </Stack>
              )}
            </Stack>
            <Stack spacing="small">
              <SectionHeading>{translate(`${TRANSLATION_PATH}.customerSupport`)}</SectionHeading>
              <Body>
                {translate(`${TRANSLATION_PATH}.contactCustomerCareAt`)}{" "}
                <a href={`tel:${customerSupportNumber}`}>{customerSupportNumber}</a>.
              </Body>
            </Stack>
          </Stack>
        </LoadingGroup.Placeholder>
      </PrintableWrapper>
    </LoadingGroup>
  );
};

const PADetailsSectionSkeleton = () => {
  return (
    <Stack spacing="mini">
      <LoadingSkeleton style={{ height: 15, width: 125 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
    </Stack>
  );
};

const PADetailsSkeleton = () => {
  return (
    <Stack spacing="large">
      <LoadingSkeleton style={{ height: 15, width: 125 }} />
      <PADetailsSectionSkeleton />
      <PADetailsSectionSkeleton />
      <PADetailsSectionSkeleton />
      <LoadingSkeleton style={{ height: 15, width: 125 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
    </Stack>
  );
};

const SectionHeading = styled.p`
  font-size: 0.75rem;
  line-height: 0.75rem;
  text-transform: uppercase;
  color: #60646d;
  font-weight: 600;
`;
